import React, { useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Button, IconButton, Stack, Typography } from "@mui/material";
import { DocumentSectionGraph } from "store/types/documents";

import { State } from "./CreateReportSection";
import { Graph } from "./Graph";

interface Props {
  data: DocumentSectionGraph["report_data"]["settings"][number][];
  onSubmit: (data: Props["data"]) => void;
  onCancel: () => void;
}

const stateToData = (state: State["graph"]): DocumentSectionGraph["report_data"]["settings"][number][] => {
  return state.map(({ chart_type, group_by, measure, ...rest }) => ({
    chart_type: chart_type!,
    group_by: group_by!,
    measure: chart_type === "scatter" ? null : measure,
    ...rest
  }));
};

export const EditGraphs: React.FC<Props> = ({ data, onSubmit, onCancel }) => {
  const [state, setState] = useState<State["graph"]>(
    data.map((item, index) => ({ ...item, id: index + 1, isDeleted: false }))
  );

  const isDisabledSubmit = useMemo(() => {
    return state
      .filter((item) => !item.isDeleted)
      .some(
        (item) =>
          !item.chart_type ||
          !item.group_by ||
          (item.chart_type !== "scatter" && !item.measure) || // Disable not scatter chart if no measure selected
          (item.chart_type === "scatter" && !(item.xAxis && item.yAxis)) // Disable scatter is no x & y axis selected
      );
  }, [state]);

  return (
    <Stack
      sx={{
        outline: "none",
        bgcolor: "white",
        minWidth: 300,
        p: "32px",
        gap: "20px",
        borderRadius: "8px",
        borderShadow: "sm",
        maxHeight: "500px",
        overflow: "hidden"
      }}
    >
      <Stack sx={{ flexDirection: "row", justifyContent: "space-between", borderBottom: "1px solid #DBE8F5" }}>
        <Typography sx={{ fontSize: "18px", fontWeight: 400, lineHeight: "24px" }}>Edit Graphs</Typography>
        <IconButton onClick={onCancel}>
          <CloseIcon />
        </IconButton>
      </Stack>
      <Stack sx={{ overflow: "auto", pt: "6px" }}>
        <Graph data={state} setData={setState} />
      </Stack>
      <Stack sx={{ flexDirection: "row", justifyContent: "flex-end", gap: "8px" }}>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => onSubmit(stateToData(state))} disabled={isDisabledSubmit}>
          Confirm
        </Button>
      </Stack>
    </Stack>
  );
};
